import { createSearchClient } from "@/configs/constants";
import { baseApi } from "@/store/baseApi";
import type { AppState } from "@/store/store";
import { getBestPrices } from "@/store/searchSlice";
import Cookies from "js-cookie";

type GetRecentlyViewedItemsProps = {
  mpIds: string[];
};

interface AlgoliaProduct {
  objectID: string;
  mp_id: number;
  title: string;
  general_rating?: number;
  number_of_general_ratings?: number;
  brand: string;
  link: string;
  image_link: string;
  price?: number;
  retail_price?: number;
}

export const recentyViewedApi = baseApi.enhanceEndpoints({}).injectEndpoints({
  endpoints: (builder) => ({
    getRecentlyViewedItems: builder.query<RecentlyViewedProduct[], GetRecentlyViewedItemsProps>({
      queryFn: async ({ mpIds }, { dispatch, getState }) => {
        if (!mpIds.length) {
          return { data: [] };
        }

        try {
          // Determine index name based on vertical ID
          const verId = process.env.NEXT_PUBLIC_VERTICAL_ID ? parseInt(process.env.NEXT_PUBLIC_VERTICAL_ID) : 1;
          const indexName = verId === 1 ? "net32_products" : "med32_products";

          // Convert mpIds from string[] to number[]
          const mpIdNumbers = mpIds.map((id) => parseInt(id)).filter((id) => !isNaN(id));

          if (mpIdNumbers.length === 0) {
            return { data: [] };
          }

          // Call Algolia directly to get product data
          const { algoliaAppId, algoliaSearchApiKey } = (getState() as AppState).config;
          if (!algoliaAppId || !algoliaSearchApiKey) {
            throw new Error(
              `Missing Algolia config: algoliaAppId=${algoliaAppId ?? "(empty)"}, algoliaSearchApiKey=${algoliaSearchApiKey ? "(present)" : "(empty)"}`,
            );
          }
          const searchClient = createSearchClient(algoliaAppId, algoliaSearchApiKey);
          const objectIds = mpIdNumbers.map((id) => id.toString());
          const attributesToRetrieve = [
            "mp_id",
            "title",
            "general_rating",
            "number_of_general_ratings",
            "brand",
            "link",
            "image_link",
            "price",
            "retail_price",
          ];

          const algoliaResults = await searchClient.getObjects<AlgoliaProduct>({
            requests: objectIds.map((objectID) => ({ indexName, objectID, attributesToRetrieve })),
          });

          // Ensure input order is maintained
          const productMap = new Map<string, AlgoliaProduct>();
          for (const result of algoliaResults.results) {
            if (result && result.mp_id) {
              productMap.set(result.mp_id.toString(), result);
            }
          }

          // Rebuild products array in the original mpIds order
          const products: AlgoliaProduct[] = [];
          for (const id of mpIdNumbers) {
            const product = productMap.get(id.toString());
            if (product) {
              products.push(product);
            }
          }

          if (products.length === 0) {
            return { data: [] };
          }

          // Dispatch getBestPrices to populate Redux stores
          // This ensures productRecommendation components can enrich with best prices from Redux
          const postalCode = Cookies.get("n32UserPrimaryZip");
          try {
            await dispatch(
              getBestPrices({
                mpIds: products.map((p) => p.mp_id),
                postalCode: postalCode || undefined,
                isBuyGetPage: false,
                tag: "",
              }),
            ).unwrap();
          } catch (error) {
            // If best prices fail to load, continue without them
            // Products will still be returned with fallback prices
            console.error("Failed to load best prices for recently viewed items:", error);
          }

          // Convert Algolia products to RecentlyViewedProduct format
          const recentlyViewedProducts: RecentlyViewedProduct[] = products.map((product) => ({
            mpId: product.mp_id,
            title: product.title,
            generalRating: product.general_rating,
            numberOfGeneralRatings: product.number_of_general_ratings,
            brand: product.brand,
            link: product.link,
            imageLink: product.image_link,
            price: product.price || 0,
            retailPrice: product.retail_price || 0,
          }));

          return { data: recentlyViewedProducts };
        } catch (error) {
          console.error("Error fetching recently viewed items:", error);
          return { data: [] };
        }
      },
    }),
  }),
});

export const { useGetRecentlyViewedItemsQuery } = recentyViewedApi;
export const { getRecentlyViewedItems } = recentyViewedApi.endpoints;

export type RecentlyViewedProduct = {
  mpId: number;
  title: string;
  generalRating?: number;
  numberOfGeneralRatings?: number;
  brand: string;
  link: string;
  imageLink: string;
  price: number;
  retailPrice: number;
};
